import {
  ComponentType,
  Dispatch,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// import Sentry SDK directly since this is published package
import * as Sentry from '@sentry/browser';
import { useIntl } from 'react-intl';
import { useRouterActions } from 'react-resource-router';

import { AnalyticsEvent, AnalyticsListener } from '@atlaskit/analytics-next';
import {
  SpotlightManager,
  SpotlightTarget,
  SpotlightTransition,
} from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ConversationAssistantButton } from '@atlassian/conversation-assistant-ui-components';
import { MultiProductDialog } from '@atlassian/product-search-dialog';

import { ChoreographedSpotlight } from 'src/components/ipm-choreographer/bitbucket-choreographer';
import { Repository, Workspace } from 'src/components/types';
import { StatsigFeatureKeys } from 'src/features';
import { useStatsigGate } from 'src/hooks/statsig';
import { useUserWorkspacePref } from 'src/hooks/use-pref';

import {
  AnalyticsEvent as LocalAnalyticsEvent,
  PageContext,
} from '../../types';
import url from '../../urls';
import { type Context as ReposContextValue } from '../contexts';
import FlagGroup, { FlagGroupItem } from '../flag-group';
import { ChatState } from '../index-next';

import { BitbucketSearchDialog } from './bitbucket-search';
import { BitbucketTabWrapper } from './bitbucket-tab';
import HorizontalPlatformSearchCompassTab from './horizontal-platform-search-compass-tab';
import HorizontalPlatformSearchCompassTabUpsell from './horizontal-platform-search-compass-tab-upsell';
import { messages } from './horizontal-platform-search.i18n';

function RovoSpotlight({
  heading,
  tryButtonText,
  onTryChatButtonPress,
  dismissButtonText,
  onDismissButtonPress,
  publishUiEvent,
  publishScreenEvent,
  children,
}: {
  heading: string;
  tryButtonText: string;
  onTryChatButtonPress: () => void;
  dismissButtonText: string;
  onDismissButtonPress: () => void;
  publishUiEvent: (event: LocalAnalyticsEvent) => void;
  publishScreenEvent: (name: string, attributes?: object) => void;
  children: ReactNode;
}) {
  useEffect(() => {
    publishScreenEvent('rovoChatSpotlight');
  }, [publishScreenEvent]);

  const publishButtonClickedEvent = (actionSubjectId: string) => {
    const event: LocalAnalyticsEvent = {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId,
      source: 'rovoChatSpotlight',
    };
    publishUiEvent(event);
  };

  return (
    <ChoreographedSpotlight
      target="rovo-assistant"
      key="rovo-assistant"
      heading={heading}
      messageId="rovo-chat-spotlight"
      targetRadius={3}
      dialogWidth={275}
      targetBgColor={token('elevation.surface')}
      dialogPlacement="bottom right"
      actions={[
        {
          onClick: () => {
            publishButtonClickedEvent('tryChat');
            onTryChatButtonPress();
          },
          text: tryButtonText,
        },
        {
          onClick: () => {
            publishButtonClickedEvent('dismiss');
            onDismissButtonPress();
          },
          text: dismissButtonText,
        },
      ]}
    >
      {children}
    </ChoreographedSpotlight>
  );
}

const styles = xcss({ margin: 'space.100' });

type RovoButtonSpotlightProps = {
  children?: ReactNode;
  onTryChat: () => void;
  publishScreenEvent: (name: string, attributes?: object) => void;
  publishUiEvent: (event: LocalAnalyticsEvent) => void;
};

export const RovoSpotlightManager = (props: RovoButtonSpotlightProps) => {
  const { children, onTryChat, publishScreenEvent, publishUiEvent } = props;
  const intl = useIntl();

  const [isSpotlightActive, setIsSpotlightActive] = useState<boolean>(false);

  const [rovoSpotlightSeen, updateRovoSpotlightPref, isRovoSpotlightLoading] =
    useUserWorkspacePref('rovo-chat-spotlight');
  const showRovoSpotlight = !isRovoSpotlightLoading && !rovoSpotlightSeen;

  useEffect(() => {
    if (showRovoSpotlight && !isSpotlightActive) {
      setIsSpotlightActive(true);
    }
  }, [showRovoSpotlight, isSpotlightActive, setIsSpotlightActive]);

  return (
    <SpotlightManager>
      <Box xcss={styles}>
        <SpotlightTarget name="rovo-assistant">{children}</SpotlightTarget>
      </Box>
      <SpotlightTransition>
        {isSpotlightActive && (
          <RovoSpotlight
            publishUiEvent={publishUiEvent}
            publishScreenEvent={publishScreenEvent}
            heading={intl.formatMessage(messages.rovoSpotlightHeading)}
            tryButtonText={intl.formatMessage(
              messages.rovoSpotlightTryChatButton
            )}
            onTryChatButtonPress={() => {
              onTryChat();
              setIsSpotlightActive(false);
              updateRovoSpotlightPref('true');
            }}
            dismissButtonText={intl.formatMessage(
              messages.rovoSpotlightDismissButton
            )}
            onDismissButtonPress={() => {
              setIsSpotlightActive(false);
              updateRovoSpotlightPref('true');
            }}
          >
            {intl.formatMessage(messages.rovoSpotlightDescription)}
          </RovoSpotlight>
        )}
      </SpotlightTransition>
    </SpotlightManager>
  );
};

type Props = {
  // It doesn't look like `isExpanded` is being used, so it's a good candidate for clean-up
  // once Nav v4 is rolled out
  /* eslint-disable react/no-unused-prop-types */
  isExpanded: boolean;
  shouldFillContainer?: boolean;
};

type PropsFromContext = ReposContextValue & {
  currentRepository?: Repository;
  currentWorkspace?: Workspace;
  isLoggedIn: boolean;
  isSearchDrawerOpen: boolean;
  isWorkspaceAdmin?: boolean;
  onSearchDrawerClose: () => void;
  onSearchDrawerOpen?: () => void;
  publishScreenEvent: (name: string, attributes?: object) => void;
  publishUiEvent: (event: LocalAnalyticsEvent) => void;
  workspaceLinkComponent?: ComponentType<PropsWithChildren<any>>;
  defaultWorkspace?: Workspace | null;
  chatState?: ChatState;
  setChatState?: Dispatch<SetStateAction<any>>;
};

export const HorizontalPlatformSearch = (props: Props & PropsFromContext) => {
  const {
    currentRepository,
    currentWorkspace,
    isLoggedIn,
    isSearchDrawerOpen,
    isWorkspaceAdmin = false,
    onSearchDrawerClose,
    onSearchDrawerOpen,
    publishScreenEvent,
    publishUiEvent,
    workspaceLinkComponent,
    defaultWorkspace,
    chatState,
    setChatState,
    repositories,
    loadRepositories,
    shouldFillContainer,
  } = props;

  const ref = useRef<HTMLInputElement>(null);
  const [cloudId, setCloudId] = useState<string>('');
  const [isLoadingCloudId, setIsLoadingCloudId] = useState<boolean>(false);
  const [flags, setFlags] = useState<FlagGroupItem[]>([]);
  const { push } = useRouterActions();
  const isCompassCrossSellDisabled = useStatsigGate(
    StatsigFeatureKeys.compassEosDisableCrossSell
  );
  const isNewCodeSearchEnabled = useStatsigGate(
    StatsigFeatureKeys.isNewCodeSearchEnabled
  );

  const workspaceSlug: string =
    currentWorkspace?.slug ||
    currentRepository?.workspace?.slug ||
    defaultWorkspace?.slug ||
    '';

  const showCompassTab = useMemo(
    () => isLoggedIn && !!workspaceSlug,
    [isLoggedIn, workspaceSlug]
  );

  useEffect(() => {
    if (!showCompassTab) {
      return;
    }

    const fetchCloudData = async () => {
      setIsLoadingCloudId(true);
      const response = await fetch(
        url.api.internal.getCompassCloudIdForWorkspace(workspaceSlug)
      );
      const result = await response.json();

      if (!result.connectedCloudIds || result.connectedCloudIds.length === 0) {
        setIsLoadingCloudId(false);
        return;
      }
      setCloudId(result.connectedCloudIds[0]);
      setIsLoadingCloudId(false);
    };

    try {
      fetchCloudData();
    } catch (e) {
      Sentry.captureException(e);
      setCloudId('');
      setIsLoadingCloudId(false);
    }
  }, [workspaceSlug, showCompassTab]);

  useEffect(() => {
    if (chatState?.isEnabled) {
      publishUiEvent({
        action: 'viewed',
        actionSubject: 'ui',
        actionSubjectId: 'rovoChatButton',
        source: 'navigation',
      });
    }
  }, [chatState?.isEnabled, publishUiEvent]);

  // Allows for global keyboard shortcut '/' to also toggle the horizontal nav search
  const setIsExpanded = (setOpen: boolean) => {
    if (setOpen === true) {
      onSearchDrawerOpen?.();
    } else {
      onSearchDrawerClose?.();
    }
  };

  const onNavigate = (_productId: string, href: string) => {
    setIsExpanded(false);
    push(href);
  };

  const currentContext: PageContext = {
    repository: currentRepository,
    workspace: currentWorkspace,
  };

  const publishAnalyticsEvent = (event: AnalyticsEvent) => {
    const { action, actionSubject, actionSubjectId } = event.payload;
    if (!event.payload.tags) {
      event.payload.tags = [];
    }
    event.payload.tags.push('fabricElements');
    publishUiEvent({
      action,
      actionSubject,
      actionSubjectId,
      ...event.payload,
      source: 'search',
    });
  };

  const onEvent = (event: AnalyticsEvent) => {
    // Do not fire event if event.payload.action is not defined. For some reason
    // this value is undefined on some of the events from the `fabric-elements`
    // channel. Adding this check to prevents a TON of sentry errors (message
    // is "Missing event.action"... so far we've had 5M+). TODO: Investigate why
    // this is sometimes undefined and how to fix (COREX-5539)
    if (!event.payload.action) {
      return;
    }

    try {
      publishAnalyticsEvent(event);
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const CompassTab = (compassTabProps: { onRetry: () => void }) => {
    if (cloudId) {
      return (
        <HorizontalPlatformSearchCompassTab
          cloudId={cloudId}
          onRetry={compassTabProps.onRetry}
        />
      );
    }

    if (isCompassCrossSellDisabled) {
      return null;
    }

    return (
      <HorizontalPlatformSearchCompassTabUpsell
        flags={flags}
        isWorkspaceAdmin={isWorkspaceAdmin}
        onRetry={compassTabProps.onRetry}
        publishUiEvent={publishUiEvent}
        setIsExpanded={setIsExpanded}
        setFlags={setFlags}
        workspaceSlug={workspaceSlug}
      />
    );
  };

  if (isNewCodeSearchEnabled) {
    return (
      <BitbucketSearchDialog
        isExpanded={isSearchDrawerOpen}
        setIsExpanded={setIsExpanded}
        onSearch={query => {
          setIsExpanded(false);
          push(url.ui.workspaceSearch(workspaceSlug, query));
        }}
        workspaceSlug={workspaceSlug}
      />
    );
  }

  return (
    <AnalyticsListener channel="fabric-elements" onEvent={onEvent}>
      <MultiProductDialog
        isExpanded={isSearchDrawerOpen}
        setIsExpanded={setIsExpanded}
        abTestCloudId={cloudId || 'bitbucket-cloud'}
        aggregatorUrl={url.api.gateway.repoSearch()}
        forwardRef={ref}
        onNavigateGeneric={onNavigate}
        shouldFillContainer={shouldFillContainer}
      >
        {({ onRetry }: { onRetry: () => void }) => (
          <>
            <BitbucketTabWrapper
              onRetry={onRetry}
              repositories={repositories}
              loadRepositories={loadRepositories}
              workspaceSlug={workspaceSlug}
              currentContext={currentContext}
              workspaceLinkComponent={workspaceLinkComponent}
              onSearchDrawerClose={onSearchDrawerClose}
            />
            {showCompassTab && !isLoadingCloudId && (
              <CompassTab onRetry={onRetry} />
            )}
          </>
        )}
      </MultiProductDialog>
      <FlagGroup setFlags={setFlags} flags={flags} />
      {/* TODO: This can be removed when Nav v4 is fully rolled out (the button is rendered elsewhere in the layout) */}
      {chatState?.isEnabled ? (
        <RovoSpotlightManager
          onTryChat={() => {
            setChatState?.((prevState: ChatState) => ({
              isActive: !prevState.isActive,
            }));
          }}
          publishScreenEvent={publishScreenEvent}
          publishUiEvent={publishUiEvent}
        >
          <ConversationAssistantButton
            productKey="bitbucket"
            isActive={chatState.isActive}
            onClick={() => {
              setChatState?.((prevState: any) => ({
                isActive: !prevState.isActive,
              }));
            }}
            siteId={chatState.siteId}
          />
        </RovoSpotlightManager>
      ) : null}
    </AnalyticsListener>
  );
};
